<template>
    <div class="banned-container">
        <div class="banned-card">
            <div class="icon-section">
                <el-icon :size="64" color="#f56c6c">
                    <WarningFilled />
                </el-icon>
            </div>
            <h1>您的账号已被封禁</h1>
            <p class="description">
                很抱歉，由于违反了社区准则或相关规定，您的账号目前已被封禁。
            </p>
            <div class="info-details">
                <div class="info-item">
                    <span class="label">封禁状态：</span>
                    <span class="value status-banned">永久封禁</span>
                </div>
                <div class="info-item">
                    <span class="label">联系方式：</span>
                    <span class="value">support@quesbank.com</span>
                </div>
            </div>

            <!-- Appeal Status Section -->
            <div v-if="appealFeedback" class="appeal-section">
                <div class="appeal-header">
                    <h3>我的申诉</h3>
                    <el-tag :type="appealStatusType" size="small" effect="dark">{{ appealStatusLabel }}</el-tag>
                </div>

                <!-- Messages -->
                <div class="appeal-messages" ref="messagesRef">
                    <div v-for="msg in appealMessages" :key="msg.id" class="msg-row"
                        :class="{ 'is-admin': msg.senderRole === 'admin' }">
                        <div class="msg-bubble" :class="msg.senderRole === 'admin' ? 'admin-msg' : 'user-msg'">
                            <div class="msg-meta">
                                <span class="msg-sender">{{ msg.senderRole === 'admin' ? '管理员' : '我' }}</span>
                                <span class="msg-time">{{ formatTime(msg.createdAt) }}</span>
                            </div>
                            <div class="msg-content">{{ msg.content }}</div>
                        </div>
                    </div>
                </div>

                <!-- Reply input (only if appeal not resolved and not waiting for admin) -->
                <div v-if="canReply" class="appeal-reply">
                    <el-input v-model="replyContent" type="textarea" :rows="2" placeholder="补充说明..." />
                    <el-button type="primary" size="small" :loading="sendingReply" :disabled="!replyContent.trim()"
                        @click="sendReplyMessage" style="margin-top:8px;">发送</el-button>
                </div>
                <div v-else-if="waitingForAdmin" class="appeal-result waiting">
                    ⏳ 您的消息已发送，请等待管理员回复后再补充说明。
                </div>

                <div v-if="appealFeedback.status === 'resolved'" class="appeal-result success">
                    🎉 您的申诉已通过，账号已解封。请刷新页面或重新登录。
                    <el-button type="success" size="small" @click="refreshPage" style="margin-top:8px;">刷新页面</el-button>
                </div>

                <div v-if="appealFeedback.status === 'rejected'" class="appeal-result rejected">
                    您的申诉已被驳回。如有异议，可重新提交申诉。
                </div>
            </div>

            <p v-if="!appealFeedback" class="footer-msg">如果您认为这是一个错误，请点击下方按钮进行申诉。</p>

            <div class="actions">
                <el-button type="primary" @click="goHome">返回首页</el-button>
                <el-button v-if="!appealFeedback || appealFeedback.status === 'rejected'" type="warning"
                    @click="showAppealDialog = true">
                    {{ appealFeedback ? '重新申诉' : '申诉解封' }}
                </el-button>
                <el-button @click="logout">退出登录</el-button>
            </div>
        </div>

        <!-- Appeal Dialog -->
        <el-dialog v-model="showAppealDialog" title="账号申诉" width="500px" append-to-body>
            <el-form :model="appealForm" label-position="top">
                <el-form-item label="申诉原因" required>
                    <el-input v-model="appealForm.content" type="textarea" :rows="4"
                        placeholder="请详细说明您的申诉理由，如有误封请提供相关证据说明..." />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="showAppealDialog = false">取消</el-button>
                <el-button type="primary" :loading="submitting" @click="submitAppeal">提交申诉</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { WarningFilled } from '@element-plus/icons-vue'
import store from '@/store'
import api from '@/utils/api'
import { ElMessage } from 'element-plus'

const router = useRouter()
const showAppealDialog = ref(false)
const submitting = ref(false)
const appealFeedback = ref(null)
const appealMessages = ref([])
const replyContent = ref('')
const sendingReply = ref(false)
const messagesRef = ref(null)

const appealForm = reactive({
    content: ''
})

const canReply = computed(() => {
    if (!appealFeedback.value) return false
    if (appealFeedback.value.status === 'resolved') return false
    // Check if last message is from user - if so, must wait for admin
    if (appealMessages.value.length > 0) {
        const lastMsg = appealMessages.value[appealMessages.value.length - 1]
        if (lastMsg.senderRole === 'user') return false
    }
    return true
})

const waitingForAdmin = computed(() => {
    if (!appealFeedback.value) return false
    if (appealFeedback.value.status === 'resolved') return false
    if (appealMessages.value.length > 0) {
        const lastMsg = appealMessages.value[appealMessages.value.length - 1]
        return lastMsg.senderRole === 'user'
    }
    return false
})

const appealStatusType = computed(() => {
    if (!appealFeedback.value) return 'info'
    const s = appealFeedback.value.status
    if (s === 'pending') return 'warning'
    if (s === 'replied') return 'success'
    if (s === 'rejected') return 'danger'
    if (s === 'resolved') return 'success'
    return 'info'
})

const appealStatusLabel = computed(() => {
    if (!appealFeedback.value) return ''
    const s = appealFeedback.value.status
    if (s === 'pending') return '待处理'
    if (s === 'replied') return '管理员已回复'
    if (s === 'rejected') return '已驳回'
    if (s === 'resolved') return '已通过解封'
    return s
})

const fetchAppealStatus = async () => {
    try {
        const res = await api.get('/api/feedbacks/mine')
        const feedbacks = res.data.feedbacks || []
        // Find the latest appeal
        const appeal = feedbacks.find(f => f.type === '申诉' || f.type === 'Account Appeal')
        if (appeal) {
            appealFeedback.value = appeal
            // Load messages
            const detail = await api.get(`/api/feedbacks/${appeal.id}`)
            appealMessages.value = detail.data.feedback.messages || []
            await nextTick()
            scrollToBottom()
        }
    } catch (err) {
        // Silent - might fail if middleware not yet updated
        console.log('Could not fetch appeal status:', err)
    }
}

const submitAppeal = async () => {
    if (!appealForm.content.trim()) {
        return ElMessage.warning('请输入申诉内容')
    }

    submitting.value = true
    try {
        await api.post('/api/feedbacks', {
            type: '申诉',
            title: '账号申诉',
            content: appealForm.content
        })
        ElMessage.success('申诉已提交，请耐心等待管理员处理')
        showAppealDialog.value = false
        appealForm.content = ''
        // Reload appeal status
        await fetchAppealStatus()
    } catch (err) {
        console.error('Appeal error:', err)
        ElMessage.error(err.response?.data?.error || '申诉提交失败')
    } finally {
        submitting.value = false
    }
}

const sendReplyMessage = async () => {
    if (!replyContent.value.trim() || !appealFeedback.value) return
    sendingReply.value = true
    try {
        const res = await api.post(`/api/feedbacks/${appealFeedback.value.id}/messages`, {
            content: replyContent.value
        })
        appealMessages.value.push(res.data.msg)
        replyContent.value = ''
        await nextTick()
        scrollToBottom()
    } catch (err) {
        ElMessage.error('发送失败')
    } finally {
        sendingReply.value = false
    }
}

const scrollToBottom = () => {
    if (messagesRef.value) {
        messagesRef.value.scrollTop = messagesRef.value.scrollHeight
    }
}

const formatTime = (ts) => {
    if (!ts) return ''
    const d = new Date(ts * 1000)
    const now = new Date()
    const isToday = d.toDateString() === now.toDateString()
    const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    if (isToday) return `今天 ${time}`
    return `${d.getMonth() + 1}/${d.getDate()} ${time}`
}

const refreshPage = () => {
    window.location.reload()
}

const goHome = () => {
    router.push('/')
}

const logout = () => {
    store.commit('CLEAR_AUTH')
    router.push('/login')
}

onMounted(() => {
    fetchAppealStatus()
})
</script>

<style scoped>
.banned-container {
    min-height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #f5f7fa;
    font-family: 'Inter', sans-serif;
    padding: 20px;
}

.banned-card {
    background: white;
    padding: 40px;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    max-width: 560px;
    width: 100%;
    text-align: center;
}

.icon-section {
    margin-bottom: 24px;
}

h1 {
    font-size: 28px;
    color: #303133;
    margin-bottom: 16px;
}

.description {
    color: #606266;
    line-height: 1.6;
    margin-bottom: 30px;
}

.info-details {
    background-color: #fef0f0;
    border: 1px solid #fde2e2;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
    text-align: left;
}

.info-item {
    margin-bottom: 12px;
}

.info-item:last-child {
    margin-bottom: 0;
}

.label {
    color: #909399;
    font-weight: 500;
}

.value {
    color: #303133;
}

.status-banned {
    color: #f56c6c;
    font-weight: bold;
}

/* Appeal section */
.appeal-section {
    text-align: left;
    border: 1px solid #e4e7ed;
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 24px;
    background: #fafbfc;
}

.appeal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.appeal-header h3 {
    margin: 0;
    font-size: 16px;
    color: #303133;
}

.appeal-messages {
    max-height: 250px;
    overflow-y: auto;
    margin-bottom: 12px;
    padding: 8px;
    background: #f0f2f5;
    border-radius: 8px;
}

.msg-row {
    display: flex;
    margin-bottom: 10px;
}

.msg-row.is-admin {
    justify-content: flex-start;
}

.msg-row:not(.is-admin) {
    justify-content: flex-end;
}

.msg-bubble {
    max-width: 80%;
    padding: 8px 12px;
    border-radius: 10px;
    font-size: 13px;
    line-height: 1.5;
}

.admin-msg {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 2px 10px 10px 10px;
}

.user-msg {
    background: #d4edda;
    border-radius: 10px 2px 10px 10px;
}

.msg-meta {
    display: flex;
    gap: 8px;
    font-size: 11px;
    color: #999;
    margin-bottom: 4px;
}

.msg-content {
    color: #303133;
    white-space: pre-wrap;
    word-break: break-word;
}

.appeal-reply {
    margin-top: 8px;
}

.appeal-result {
    padding: 12px;
    border-radius: 8px;
    margin-top: 12px;
    font-size: 14px;
    text-align: center;
}

.appeal-result.success {
    background: #f0f9eb;
    color: #67c23a;
    border: 1px solid #e1f3d8;
}

.appeal-result.rejected {
    background: #fef0f0;
    color: #f56c6c;
    border: 1px solid #fde2e2;
}

.appeal-result.waiting {
    background: #fdf6ec;
    color: #e6a23c;
    border: 1px solid #faecd8;
}

.footer-msg {
    color: #909399;
    font-size: 14px;
    margin-bottom: 24px;
}

.actions {
    display: flex;
    gap: 16px;
    justify-content: center;
    flex-wrap: wrap;
}
</style>
